/**
 * city/buildings/tube-house-classic.js
 * Dựng nhà phố ống truyền thống & hiện đại phong cách Việt Nam
 */

import * as THREE from 'three';
import { createRng, hashString, shade } from '../builder-core.js';

class MethodsHolder {
  createTubeHouseClassic(options = {}) {
    const house = new THREE.Group();
    house.name = 'TubeHouse_Classic_VN';
    const w = options.width || 4.0;
    const d = options.depth || 5.8;
    const G = 3.5,
      F = 3.0,
      H = G + F;
    const fz = d / 2;
    const storeName = options.storeName || 'Phở Bò Gia Truyền';
    const wall = options.wallColor || '#f2c14e';
    const seed = hashString('classic|' + storeName);
    const rnd = createRng(seed);

    const frontMat = this._houseBody(house, w, H, d, wall, seed, 0.9);
    const trimLight = this.mat(shade(wall, 0.5), { roughness: 0.85 });
    const trimDark = this.mat(shade(wall, -0.18), { roughness: 0.9 });

    // TẦNG TRỆT: trụ biên, cửa cuốn kéo lên lộ quán bên trong, bậc thềm
    [-1, 1].forEach((s) => this.box(house, 0.24, G, 0.14, trimDark, s * (w / 2 - 0.12), G / 2, fz + 0.07));
    const openW = w - 0.56,
      doorH = 2.55;
    this.box(
      house,
      openW,
      doorH,
      0.04,
      this.texMat(this.getInteriorTexture('pho', seed), { color: '#e5e5e5' }, true),
      0,
      doorH / 2,
      fz + 0.02,
      null,
      false
    );
    this.box(
      house,
      openW,
      0.7,
      0.06,
      this.texMat(this.getRollerDoorTexture(), { metalness: 0.5, roughness: 0.45 }),
      0,
      doorH - 0.35,
      fz + 0.05
    );
    this.box(
      house,
      openW + 0.12,
      0.24,
      0.3,
      this.mat('#a8a29e', { metalness: 0.4, roughness: 0.5 }),
      0,
      doorH + 0.12,
      fz + 0.13
    );
    this.box(house, w - 0.1, 0.1, 0.45, this.mat('#a8a29e', { roughness: 0.9 }), 0, 0.05, fz + 0.22);

    this._addAwning(house, {
      y: doorH + 0.38,
      z: fz,
      w: w - 0.3,
      depth: 1.25,
      drop: 0.5,
      colorA: options.awningColor || '#dc2626',
      colorB: '#f8fafc'
    });
    this._addSign(house, {
      text: storeName,
      sub: options.signSub || 'Sáng · Trưa · Tối — ĐT: 0912 345 678',
      bg: options.signColor || '#b91c1c',
      fg: '#fef08a',
      border: '#fde047',
      w: w - 0.3,
      h: 0.54,
      y: 3.2,
      z: fz + 0.07
    });
    this.box(house, w + 0.1, 0.16, 0.24, trimLight, 0, G, fz + 0.12);

    // TẦNG 2: cửa đi 2 cánh chớp xanh kiểu Pháp, ban công bao chuồng cọp
    this._addWindow(house, {
      y: G + 1.25,
      z: fz + 0.01,
      w: 1.15,
      h: 2.1,
      frame: '#f5f5f4',
      shutters: options.shutterColor || '#2f6b3a',
      sill: false,
      cols: 2,
      rows: 3,
      glass: 'home'
    });
    this._addBalcony(house, {
      y: G + 0.08,
      z: fz,
      w: w - 0.1,
      depth: 0.9,
      slabColor: '#d6d3d1',
      rail: 'concrete',
      railH: 0.25
    });
    this._addIronCage(house, { y: G + 0.08, z: fz, w: w - 0.1, depth: 0.9, h: 2.45 });
    // Quần áo phơi trong lồng
    this.box(house, 1.3, 0.012, 0.012, this.mat('#27272a'), -1.0, G + 2.25, fz + 0.55, null, false);
    [
      ['#2563eb', -1.45],
      ['#f472b6', -1.1],
      ['#facc15', -0.75]
    ].forEach(([c, cx]) => {
      this.box(house, 0.26, 0.36, 0.03, this.mat(c, { roughness: 0.9 }), cx, G + 2.05, fz + 0.55);
    });
    this._addPlanter(house, 1.3, G + 0.1, fz + 0.62, 0.6, rnd);
    this._addAcUnit(house, 1.3, G + 2.0, fz + 0.16);

    // GỜ MÁI & TƯỜNG CHẮN MÁI HOA VĂN
    this.box(house, w + 0.12, 0.16, 0.28, trimLight, 0, H, fz + 0.12);
    this.box(house, w, 0.62, 0.16, frontMat, 0, H + 0.31, fz - 0.08);
    for (let i = -1; i <= 1; i++)
      this.box(house, w / 3 - 0.3, 0.3, 0.02, trimDark, i * (w / 3), H + 0.33, fz + 0.005, null, false);
    this.box(house, w + 0.1, 0.08, 0.26, trimLight, 0, H + 0.66, fz - 0.08);
    [-1, 1].forEach((s) =>
      this.box(house, 0.14, 0.62, d - 0.2, this.mat('#a8a29e'), s * (w / 2 - 0.07), H + 0.31, -0.1)
    );

    // TUM THANG, MÁI TÔN, BỒN NƯỚC INOX & ĂNG-TEN
    const tumW = w * 0.5,
      tumD = d * 0.4,
      tumX = -w * 0.2,
      tumZ = -d * 0.22;
    this.box(house, tumW, 2.3, tumD, frontMat, tumX, H + 1.15, tumZ);
    this.box(
      house,
      0.75,
      1.9,
      0.05,
      this.texMat(this.getWoodPanelTexture('#7c2d12')),
      tumX,
      H + 0.95,
      tumZ + tumD / 2 + 0.02
    );
    this.box(
      house,
      tumW + 0.3,
      0.05,
      tumD + 0.5,
      this.texMat(this.getToleTexture('#2563eb'), { metalness: 0.4, roughness: 0.45 }),
      tumX,
      H + 2.36,
      tumZ + 0.1,
      [0.12, 0, 0]
    );
    this._addWaterTank(house, w * 0.22, H, -d * 0.3);
    const ant = this.mat('#71717a', { metalness: 0.7, roughness: 0.4 });
    this.box(house, 0.03, 1.2, 0.03, ant, tumX - 0.4, H + 2.95, tumZ, null, false);
    [0, 0.2, 0.4].forEach((ay, i) =>
      this.box(house, 0.7 - i * 0.15, 0.02, 0.02, ant, tumX - 0.4, H + 3.2 + ay, tumZ, null, false)
    );

    return house;
  }

  // 3.2 NHÀ PHỐ HIỆN ĐẠI 3 TẦNG: khung hộp nhô, lam gỗ dọc, ban công kính, pergola sân thượng
  createTubeHouseModern(options = {}) {
    const house = new THREE.Group();
    house.name = 'TubeHouse_Modern_VN';
    const w = options.width || 4.2;
    const d = options.depth || 5.8;
    const G = 3.6,
      F = 3.2,
      H = G + 2 * F;
    const fz = d / 2;
    const storeName = options.storeName || 'Kiến Trúc & Nội Thất';
    const wall = options.wallColor || '#f5f5f4';
    const seed = hashString('modern|' + storeName);
    const rnd = createRng(seed);

    const frontMat = this._houseBody(house, w, H, d, wall, seed, 0.25);
    const dark = this.mat('#27313f', { roughness: 0.6 });
    const alu = this.mat('#111827', { roughness: 0.4, metalness: 0.5 });
    const wood = this.mat('#b0622f', { roughness: 0.6 });

    // TẦNG TRỆT: mặt kính showroom khung nhôm đen
    const shopW = w - 0.4,
      shopH = 2.75;
    this.box(
      house,
      shopW,
      shopH,
      0.04,
      this.texMat(this.getInteriorTexture('showroom', seed), {}, true),
      0,
      shopH / 2,
      fz + 0.02,
      null,
      false
    );
    this.box(
      house,
      shopW,
      shopH,
      0.01,
      this.mat('#e0f2fe', { transparent: true, opacity: 0.12, roughness: 0.05, metalness: 0.3 }),
      0,
      shopH / 2,
      fz + 0.05,
      null,
      false
    );
    this.box(house, shopW + 0.1, 0.08, 0.1, alu, 0, shopH, fz + 0.05);
    this.box(house, shopW + 0.1, 0.06, 0.1, alu, 0, 0.03, fz + 0.05);
    [-1, 1].forEach((s) => {
      this.box(house, 0.08, shopH, 0.1, alu, s * (shopW / 2 + 0.01), shopH / 2, fz + 0.05);
      this.box(house, 0.05, shopH, 0.08, alu, (s * shopW) / 6, shopH / 2, fz + 0.05, null, false);
      this.box(
        house,
        0.03,
        0.8,
        0.05,
        this.mat('#d4d4d8', { metalness: 0.9, roughness: 0.2 }),
        s * 0.12,
        1.15,
        fz + 0.1,
        null,
        false
      );
    });
    this._addSign(house, {
      text: storeName,
      sub: options.signSub || 'Thiết kế · Thi công · Nội thất',
      bg: options.signColor || '#1e3a8a',
      fg: '#e0f2fe',
      w: w - 0.3,
      h: 0.55,
      y: shopH + 0.4,
      z: fz + 0.09,
      depth: 0.16
    });
    this.box(house, w + 0.06, 0.26, 0.5, dark, 0, G, fz + 0.25);

    // KHUNG HỘP NHÔ TẦNG 2-3
    [-1, 1].forEach((s) => this.box(house, 0.24, 2 * F, 0.55, dark, s * (w / 2 - 0.12), G + F, fz + 0.27));
    this.box(house, w + 0.06, 0.3, 0.55, dark, 0, H - 0.15, fz + 0.27);

    // BÊN TRÁI: vách lam gỗ dọc che nắng phủ 2 tầng
    const innerL = -w / 2 + 0.24,
      innerR = w / 2 - 0.24;
    const splitX = innerL + (innerR - innerL) * 0.52;
    const slatW = splitX - innerL;
    this.box(
      house,
      slatW,
      2 * F - 0.3,
      0.04,
      this.glassMat('home'),
      (innerL + splitX) / 2,
      G + F,
      fz + 0.02,
      null,
      false
    );
    [G + 0.25, H - 0.4].forEach((ry) => this.box(house, slatW, 0.06, 0.06, alu, (innerL + splitX) / 2, ry, fz + 0.34));
    const slatCount = Math.floor(slatW / 0.15);
    for (let i = 0; i < slatCount; i++) {
      this.box(
        house,
        0.07,
        2 * F - 0.4,
        0.14,
        wood,
        innerL + 0.08 + i * ((slatW - 0.16) / (slatCount - 1)),
        G + F - 0.05,
        fz + 0.42
      );
    }

    // BÊN PHẢI: tầng 2 cửa kính lớn, tầng 3 logia ban công kính + điều hòa + cây
    const rw = innerR - splitX - 0.05;
    const rx = (innerR + splitX) / 2 + 0.025;
    this._addWindow(house, {
      x: rx,
      y: G + 1.5,
      z: fz + 0.01,
      w: rw - 0.16,
      h: 2.3,
      frame: '#111827',
      cols: 2,
      sill: false,
      glass: 'home'
    });
    this._addBalcony(house, {
      x: rx,
      y: G + F + 0.11,
      z: fz,
      w: rw,
      depth: 0.5,
      rail: 'glass',
      slabColor: '#27313f',
      railColor: '#111827'
    });
    this._addWindow(house, {
      x: rx,
      y: G + F + 1.4,
      z: fz + 0.01,
      w: rw - 0.16,
      h: 2.2,
      frame: '#111827',
      cols: 2,
      sill: false,
      glass: 'home'
    });
    this._addAcUnit(house, rx + rw / 2 - 0.42, G + F + 0.39, fz + 0.2);
    this._addPlanter(house, rx - rw / 2 + 0.32, G + F + 0.11, fz + 0.25, 0.45, rnd);

    // MÁI: tường chắn mái, giàn pergola gỗ & bồn cây sân thượng
    this.box(house, w, 0.9, 0.18, frontMat, 0, H + 0.45, fz - 0.09);
    this.box(house, w + 0.08, 0.08, 0.28, dark, 0, H + 0.94, fz - 0.09);
    [-1, 1].forEach((s) =>
      this.box(house, 0.14, 0.9, d - 0.2, this.mat('#a8a29e'), s * (w / 2 - 0.07), H + 0.45, -0.1)
    );
    [-1, 1].forEach((s) =>
      [-0.4, -2.3].forEach((pz) => this.box(house, 0.1, 2.2, 0.1, dark, s * (w / 2 - 0.35), H + 1.1, pz))
    );
    for (let i = 0; i < 9; i++) this.box(house, w - 0.5, 0.06, 0.1, wood, 0, H + 2.23, -0.3 - i * 0.24);
    this._addPlanter(house, 0, H, fz - 0.45, 1.6, rnd);

    return house;
  }
}

export function registerTubeHouseClassicMethods(proto) {
  Object.getOwnPropertyNames(MethodsHolder.prototype).forEach((name) => {
    if (name !== 'constructor') {
      proto[name] = MethodsHolder.prototype[name];
    }
  });
}
